/**
 * Product Card Swatch Image Switcher
 * Handles updating product card images when color swatches are clicked
 */

class ProductCardSwatch extends HTMLElement {
  constructor() {
    super();
    this.productCard = this.closest('.product-card');
    if (!this.productCard) return;

    this.imageWrapper = this.productCard.querySelector('.product-card__image-wrapper');
    this.mainImage = this.productCard.querySelector('.product-card__image--main img');
    this.secondImage = this.productCard.querySelector('.product-card__image--second img');
    this.swatches = this.querySelectorAll('.swatch-color');

    this.originalMainImageSrc = this.mainImage?.currentSrc || this.mainImage?.src;
    this.originalSecondImageSrc = this.secondImage?.currentSrc || this.secondImage?.src;

    this.init();
  }

  init() {
    if (!this.mainImage || this.swatches.length === 0) return;

    this.swatches.forEach((swatch) => {
      swatch.addEventListener('click', this.handleSwatchClick.bind(this));
      swatch.addEventListener('mouseenter', this.handleSwatchHover.bind(this));
    });

    this.setDefaultActiveState();
  }

  setDefaultActiveState() {
    const currentSrc = (this.mainImage.currentSrc || this.mainImage.src || '').split('?')[0];
    const matchingSwatch = Array.from(this.swatches).find((swatch) => {
      const variantImage = (swatch.dataset.variantImage || '').split('?')[0];
      return variantImage && (currentSrc === variantImage || currentSrc.includes(variantImage) || variantImage.includes(currentSrc));
    });
    const swatchToActivate = matchingSwatch || this.swatches[0];
    if (swatchToActivate) this.updateActiveState(swatchToActivate);
  }

  handleSwatchClick(event) {
    event.preventDefault();
    const swatch = event.currentTarget;
    const variantData = this.getVariantData(swatch);

    if (!variantData) return;

    this.updateActiveState(swatch);

    this.updateProductImage(variantData);

    this.updateProductUrl(variantData);
    this.updateProductAndVariantIds(variantData);
  }

  handleSwatchHover(event) {
    const swatch = event.currentTarget;
    const variantData = this.getVariantData(swatch);

    if (!variantData) return;
    
    if (this.secondImage && variantData.secondImage) {
      const imageWidth = this.secondImage.naturalWidth || 900;
      this.secondImage.src = this.getImageUrl(variantData.image, imageWidth);

      if (this.secondImage.srcset) {
        this.secondImage.srcset = this.generateSrcset(variantData.image);
      }
    }
  }

  getVariantData(swatch) {
    const variantIndex = swatch.dataset.index;
    const variantId = swatch.dataset.variantId;
    const variantImage = swatch.dataset.variantImage;
    const variantSecondImage = swatch.dataset.variantSecondImage;
    const variantUrl = swatch.dataset.variantUrl || swatch.href;

    if (!variantImage) return null;

    return {
      index: variantIndex,
      id: variantId,
      image: variantImage,
      secondImage: variantSecondImage,
      url: variantUrl
    };
  }

  updateActiveState(clickedSwatch) {
    this.swatches.forEach(swatch => swatch.classList.remove('active'));
    clickedSwatch.classList.add('active');
  }

  updateProductImage(variantData) {
    if (!this.mainImage || !variantData.image) return;

    setTimeout(() => {
      const imageWidth = this.mainImage.naturalWidth || 900;
      this.mainImage.src = this.getImageUrl(variantData.image, imageWidth);

      if (this.mainImage.srcset) {
        this.mainImage.srcset = this.generateSrcset(variantData.image);
      }

      const altText = this.mainImage.alt.split(' - ')[0];
      this.mainImage.alt = `${altText} - ${variantData.url?.split('variant=')[1] || ''}`;

      this.mainImage.parentElement.style.opacity = '1';
  
      if (this.secondImage && variantData.secondImage) {
        const imageWidth = this.secondImage.naturalWidth || 900;
        this.secondImage.src = this.getImageUrl(variantData.secondImage, imageWidth);

        if (this.secondImage.srcset) {
          this.secondImage.srcset = this.generateSrcset(variantData.secondImage);
        }
      }
    }, 150);
  }

  resetToOriginalImage() {
    if (!this.mainImage) return;

    this.mainImage.style.opacity = '0';

    setTimeout(() => {
      this.mainImage.src = this.originalMainImageSrc;
      this.mainImage.style.opacity = '1';
    }, 150);

    if (this.secondImage && this.originalSecondImageSrc) {
      this.secondImage.style.opacity = '0';

      setTimeout(() => {
        this.secondImage.src = this.originalSecondImageSrc;
        this.secondImage.style.opacity = '1';
      }, 150);
    }
  }

  updateProductUrl(variantData) {
    if (!variantData.url) return;

    const productLink = this.productCard.querySelector('a[href*="/products/"]');
    if (productLink) {
      productLink.href = variantData.url;
    }
  }

  updateProductAndVariantIds(variantData) {
    const card = this.productCard;
    const { productId, id: variantId } = variantData;
    const modal = card.querySelector('[id^="QuickView-"]');

    if (modal && productId) {
      const sectionId = modal.id.split('-').pop();
      const modalId = `QuickView-${productId}-${sectionId}`;
      const prevId = modal.id;
      modal.id = modalId;
      card.querySelectorAll(`[aria-controls="${prevId}"]`).forEach((el) => el.setAttribute('aria-controls', modalId));
      const form = card.querySelector('form[id^="quick-add-"]');
      if (form) form.id = `quick-add-${sectionId}${productId}`;
    }

    if (variantId) {
      const input = card.querySelector('input[name="id"].product-variant-id');
      if (input) input.value = variantId;
      card.querySelectorAll('[data-product-id]').forEach((el) => el.setAttribute('data-product-id', variantId));
    }
  }

  getImageUrl(imageUrl, width) {
    const urlParts = imageUrl.split('?')[0];
    return `${urlParts}?width=${width}`;
  }

  generateSrcset(imageUrl) {
    const widths = [165, 360, 535, 750, 900, 1100, 1500];
    const baseUrl = imageUrl.split('?')[0];

    return widths
      .map(width => `${baseUrl}?width=${width} ${width}w`)
      .join(', ');
  }
}

if (!customElements.get('product-card-swatch')) {
  customElements.define('product-card-swatch', ProductCardSwatch);
}

